import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { isWorksheet } from '@/main/routing.utilities';
import { SUPPORT_LINK } from '@/utilities/externalLinks';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { isViewOnlyWorkbookMode } from '@/utilities/workbookMode.utilities';
import { setTemplateWorkstepId, setUserPreferencesDisplay } from '@/workbench/workbench.actions';
import { determineIsOwner } from '@/workbook/workbook.utilities';
import { useCurrentWorkstepId } from '@/worksteps/worksteps.store';
import { Button, ExternalLink, Modal } from '@seeqdev/qomponents';

interface WorksheetPreferencesModalProps {
  onClose: () => void;
}

/**
 * Modal to save current worksheet configuration as the default for newly created worksheets.
 */
export const WorksheetPreferencesModal = ({ onClose }: WorksheetPreferencesModalProps) => {
  const { t } = useTranslation();
  const { isTopic, owner } = useFlux(sqWorkbookStore);
  const isWorksheetOrViewOnly = isWorksheet() || isViewOnlyWorkbookMode();
  const { currentUser } = useFlux(sqWorkbenchStore);
  const currentWorkstepId = useCurrentWorkstepId();

  const isOwner = determineIsOwner(owner, currentUser);
  const canSavePreferences = isWorksheetOrViewOnly && isOwner && !isTopic;

  const handleSavePreferences = () => {
    setTemplateWorkstepId(currentWorkstepId!)
      .then(() => {
        successToast({ messageKey: 'USER.WORKSHEET_PREFERENCES_SAVED_TOAST' });
        setUserPreferencesDisplay(false);
        onClose();
      })
      .catch((e) => errorToast({ httpResponseOrError: e }));
  };

  const handleResetPreferences = () => {
    if (canSavePreferences) {
      setTemplateWorkstepId(SeeqNames.Database.Utils.EmptyGuid)
        .then(() => {
          successToast({ messageKey: 'USER.WORKSHEET_PREFERENCES_RESTORED_TOAST' });
          setUserPreferencesDisplay(false);
          onClose();
        })
        .catch((e) => errorToast({ httpResponseOrError: e }));
    }
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      testId="worksheetPreferencesModal"
      title={t('USER.WORKSHEET_PREFERENCES')}
      modalFooter={
        <div className="flexColumnContainer flexCenter width-maximum tw:gap-2">
          <Button
            onClick={onClose}
            label={t('CANCEL')}
            size="sm"
            testId="cancelButton"
            extraClassNames="min-width-100"
          />
          <Button
            onClick={handleSavePreferences}
            label={t('USER.SAVE_PREFERENCES_BUTTON')}
            size="sm"
            variant="theme"
            disabled={!canSavePreferences}
            tooltip={t('USER.SAVE_PREFERENCES_BUTTON_TOOLTIP')}
            testId="saveWorksheetDefaults"
            extraClassNames="min-width-100"
          />
        </div>
      }>
      <div className="flexRowContainer overflowHidden" data-testid="worksheetPreferencesContent">
        <div className="pb10 flexColumnContainer flexSpaceBetween">
          <b className="mb5">{t('USER.SAVE_PREFERENCES_TITLE')}</b>
          {canSavePreferences && (
            <FakeLink
              testId="resetWorksheetDefaults"
              onClick={handleResetPreferences}
              extraClassNames="mb5 link sq-force-text-gray">
              <u>{t('USER.SAVE_PREFERENCES_RESET')}</u>
            </FakeLink>
          )}
        </div>
        <div>
          {t('USER.SAVE_PREFERENCES_DETAILS')}
          <ul className="mb0">
            <li>{t('USER.SAVE_PREFERENCES_BULLET_ONE')}</li>
            <li>{t('USER.SAVE_PREFERENCES_BULLET_TWO')}</li>
            <li>{t('USER.SAVE_PREFERENCES_BULLET_THREE')}</li>
          </ul>
          <ExternalLink href={`${SUPPORT_LINK}/worksheet-preferences`}>
            {t('USER.SAVE_PREFERENCES_KB_LINK')}
          </ExternalLink>
        </div>
        {!canSavePreferences && (
          <div className="mt10 textAlignCenter xsmall">
            (
            {t(
              isWorksheetOrViewOnly && !isTopic
                ? 'USER.SAVE_PREFERENCES_PERMISSIONS_ERROR'
                : 'USER.SAVE_PREFERENCES_DISABLED',
            )}
            )
          </div>
        )}
      </div>
    </Modal>
  );
};
